import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';

import type { SkuRulePreviewRequest } from '@nucleus-commerce/api-client/admin';

import { byRulePosition } from '../api/sku-rule-mappers';
import { skuRulesApi } from '../api/sku-rules-api';

import { dictionaryKeys } from './dictionary-keys';

export function skuRulesQueryOptions() {
  return queryOptions({
    queryKey: dictionaryKeys.skuRules(),
    queryFn: () => skuRulesApi.list().then((rows) => rows.sort(byRulePosition)),
    staleTime: 5 * 60_000,
  });
}

export function useSkuRules() {
  return useQuery(skuRulesQueryOptions());
}

export function skuRuleQueryOptions(id: string) {
  return queryOptions({
    queryKey: dictionaryKeys.skuRule(id),
    queryFn: () => skuRulesApi.get(id),
    staleTime: 5 * 60_000,
  });
}

export function useSkuRule(id: string) {
  return useQuery(skuRuleQueryOptions(id));
}

/** Which rule a product of this type gets and its next style number; null asks for the default rule. */
export function skuRuleResolveQueryOptions(productTypeId: string | null) {
  return queryOptions({
    queryKey: dictionaryKeys.skuRuleResolve(productTypeId),
    queryFn: () => skuRulesApi.resolve(productTypeId),
    staleTime: 60_000,
  });
}

export function useSkuRuleResolve(productTypeId: string | null, enabled = true) {
  return useQuery({ ...skuRuleResolveQueryOptions(productTypeId), enabled });
}

/**
 * Samples for unsaved templates, keyed by the request so typing never shows another request's
 * answer; the previous answer stays on screen while the next one loads.
 */
export function useSkuRulePreview(body: SkuRulePreviewRequest, enabled: boolean) {
  return useQuery({
    queryKey: dictionaryKeys.skuRulePreview(body),
    queryFn: () => skuRulesApi.preview(body),
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function skuRuleRunsQueryOptions(ruleId: string) {
  return queryOptions({
    queryKey: dictionaryKeys.skuRuleRuns(ruleId),
    queryFn: () => skuRulesApi.listRuns(ruleId),
    staleTime: 0,
  });
}

export function useSkuRuleRuns(ruleId: string, enabled = true) {
  return useQuery({ ...skuRuleRunsQueryOptions(ruleId), enabled });
}
